import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { isKey, Key } from '@app/shared/key';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'dgx-profile-skills-list-target-rating',
  templateUrl: './profile-skills-list-target-rating.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListTargetRatingComponent {
  @Input() public tag: TagsApi.Tag;
  @Input() public isOwner: boolean;
  @Input() public isEventHandler: boolean = true;
  @Input() public proficiencyTargetLevel: string;
  @Output() public tagRatingChange = new EventEmitter<any>();

  public i18n = this.translateService.instant([
    'dgTagRating_AddSkillTarget',
    'PersonalSkillView_SkillTarget',
  ]);

  constructor(
    private tagRatingService: TagRatingService,
    private translateService: TranslateService,
    private tagsService: TagsService
  ) {}

  public get level(): string | undefined {
    const rating = this.tag.ratings?.find(
      (rating) => rating.type === InternalTagRatingTypes.target
    );
    return this.proficiencyTargetLevel || rating?.level;
  }

  public get showDefaultPlus(): boolean {
    return !this.level && this.isOwner;
  }

  public get tabIndex(): 0 | undefined {
    return this.isOwner ? 0 : undefined;
  }

  public get role(): 'button' | undefined {
    return this.isOwner ? 'button' : undefined;
  }

  public get ariaLabel(): string {
    let label = '';
    if (this.level) {
      label = this.translateService.instant('dgTagRating_A11ySkillTarget', {
        skillName: this.tag.title,
        levelNumber: this.level,
      });
    } else {
      label = this.translateService.instant('dgTagRating_A11yAddSkillTarget', {
        skillName: this.tag.title,
      });
    }
    return label;
  }

  public get tooltip(): string {
    return this.level
      ? this.i18n.PersonalSkillView_SkillTarget
      : this.i18n.dgTagRating_AddSkillTarget;
  }

  public clickHandler(event: MouseEvent): void {
    this.setRating(event);
  }

  public keydownHandler(event: KeyboardEvent): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling screen
      event.preventDefault();
      this.setRating(event);
    }
  }

  private setRating(event: Event): void {
    if (!this.isOwner) {
      return;
    }
    event.stopPropagation();
    const tagCopy = {
      ...cloneDeep(this.tag),
      rating: { level: this.level } as TagsApi.UserTagRating,
    };
    const title = this.translateService.instant(
      'PersonalSkillView_SkillTargetModalTitleFormat',
      {
        skill: this.tag.name,
      }
    );
    const modal = this.tagRatingService.openRatingModal({
      event,
      title,
      tag: tagCopy,
      ratingType: InternalTagRatingTypes.target,
    });

    modal.subscribe((rating: TagsApi.UserTagRating) => {
      this.tagsService
        .rateTag(
          rating.level as unknown as number,
          InternalTagRatingTypes.target,
          this.tag as TagsApi.TagDetails,
          null,
          event.target as HTMLElement
        )
        .subscribe(() => {
          this.tagRatingChange.emit({
            name: this.tag.name,
            level: rating.level,
          });
        });
    });
  }

  public getLevelDescription(level: string): string {
    return this.tagsService.orgLevels.find((l) => l.value.toString() === level)
      ?.description;
  }
}
